import { JSX, useCallback, useEffect, useState } from "react";

import { IconButton, TableCell } from "@mui/material";
import TableRow from "@mui/material/TableRow";

import DeleteIcon from "@mui/icons-material/Delete";

import IndexCheckbox from "utils/elements/IndexCheckbox";
import { AddEvent, ChangeEvent, Event, EventGroup } from "utils/history/Event";
import { escapeKey } from "utils/PatchUtils";

import { ReportRight } from "core/blueprint/trajectory/TrajectoryBlueprintService";
import { calculateRoleRightStatus } from "core/trajectory/access/AccessUtil";
import { RoleRowProps } from "core/trajectory/access/RoleTable";

export type ReportRoleRowProps = RoleRowProps & {
	selected: string[];
};

const RIGHTS_SIZE = 4;
const DEFAULT_RIGHTS = Array(RIGHTS_SIZE).fill(false);

export default function ReportRoleRow(props: ReportRoleRowProps): JSX.Element {
	const [rightsOverview, setRightsOverview] = useState(DEFAULT_RIGHTS);

	useEffect(() => {
		const rights = props.role.reportRights;
		if (rights === undefined) {
			return;
		}

		const selected = props.selected;
		const status = calculateRoleRightStatus(rights, selected, RIGHTS_SIZE);
		setRightsOverview(status);
	}, [props.selected, props.role.reportRights]);

	const toggleRight = useCallback(
		(right: ReportRight, enabled: boolean): void => {
			const basePath = `/roles/${props.index}/reportRights/`;

			const events: Event[] = [];
			for (const key of props.selected) {
				const currentRights = props.role.reportRights[key]?.[""];

				// We have to escape the key since this is the name of the report.
				const escapedKey = escapeKey(key);
				if (props.role.reportRights[key] === undefined) {
					events.push(new AddEvent(`${basePath}${escapedKey}`, {}));
				}

				const keyPath = `${basePath}${escapedKey}/`;
				if (currentRights === undefined) {
					events.push(new AddEvent(keyPath, DEFAULT_RIGHTS));
				}

				const rights = getImpliedRights(right, enabled);
				for (const [impliedRight, status] of rights.entries()) {
					const path = `${keyPath}/${impliedRight}`;
					const hasRight = currentRights?.[impliedRight] ?? false;

					if (status !== hasRight) {
						events.push(new ChangeEvent(path, !status, status));
					}
				}
			}

			const group = new EventGroup(events);
			props.onChange(group);
		},
		[props.index, props.role.reportRights, props.selected, props.onChange]
	);

	function getImpliedRights(
		right: ReportRight,
		enabled: boolean
	): Map<ReportRight, boolean> {
		const rights = new Map<ReportRight, boolean>();
		rights.set(right, enabled);

		if (enabled) {
			if (right === ReportRight.ADMIN) {
				rights.set(ReportRight.WRITE, true);
				rights.set(ReportRight.READ, true);
				rights.set(ReportRight.HIDDEN, false);
			}
			if (right === ReportRight.WRITE) {
				rights.set(ReportRight.READ, true);
				rights.set(ReportRight.HIDDEN, false);
			}
			if (right === ReportRight.READ) {
				rights.set(ReportRight.HIDDEN, false);
			}
			if (right === ReportRight.HIDDEN) {
				rights.set(ReportRight.ADMIN, false);
				rights.set(ReportRight.WRITE, false);
				rights.set(ReportRight.READ, false);
			}
		} else {
			if (right === ReportRight.READ) {
				rights.set(ReportRight.ADMIN, false);
				rights.set(ReportRight.WRITE, false);
			}
			if (right === ReportRight.WRITE) {
				rights.set(ReportRight.ADMIN, false);
			}
		}

		return rights;
	}

	const onDelete = useCallback((): void => {
		props.onDelete(props.index);
	}, [props.index, props.onDelete]);

	function isDisabled(): boolean {
		return !props.editable || props.selected.length === 0;
	}

	return (
		<TableRow>
			<TableCell component="th" scope="row">
				{props.role.name}
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={ReportRight.ADMIN}
					disabled={isDisabled()}
					status={rightsOverview[ReportRight.ADMIN]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={ReportRight.WRITE}
					disabled={isDisabled()}
					status={rightsOverview[ReportRight.WRITE]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={ReportRight.READ}
					disabled={isDisabled()}
					status={rightsOverview[ReportRight.READ]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="checkbox" align="center">
				<IndexCheckbox
					index={ReportRight.HIDDEN}
					disabled={isDisabled()}
					status={rightsOverview[ReportRight.HIDDEN]}
					onChange={toggleRight}
				/>
			</TableCell>
			<TableCell padding="none" align="right">
				<IconButton
					aria-label="delete"
					size="small"
					disabled={!props.editable}
					onClick={onDelete}>
					<DeleteIcon fontSize="inherit"></DeleteIcon>
				</IconButton>
			</TableCell>
		</TableRow>
	);
}
